import { useEffect, useRef } from 'react';
import useGooglePayScript from '../../../hooks/payment/useGooglePayScript';
import useTheme from '../../../hooks/ui/useTheme';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import LoadingIndicator from '../../loading/LoadingIndicator';
import Theme from '../../Theme';
import classnames from 'classnames';

interface Props {
  onClick: () => void;
  loading: boolean;
  disabled?: boolean;
  silentlyDisabled?: boolean;
  className?: string;
}

export default function GooglePayButton({ onClick, loading, disabled, silentlyDisabled, className }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { loaded } = useGooglePayScript();
  const { isDarkMode } = useTheme();
  const { isMobileApp } = useMobileApp();

  // The Google-hosted button is built once and keeps whatever handler it was given, so it must
  // call through a ref: a handler captured at build time holds that render's props, e.g. a
  // `useStripe()` that was still null while <Elements> resolved (SHOP-WEB-29).
  const handleClickRef = useRef<() => void>(null);
  handleClickRef.current = () => {
    if (disabled || silentlyDisabled || loading) return;
    onClick();
  };

  useEffect(() => {
    if (!loaded || !containerRef.current) return;

    const client = new google.payments.api.PaymentsClient({ environment: 'PRODUCTION' });
    const button = client.createButton({
      onClick: () => handleClickRef.current?.(),
      buttonColor: isDarkMode ? 'white' : 'black',
      buttonType: 'pay',
      buttonSizeMode: 'fill',
      buttonRadius: 9999,
    });

    containerRef.current.appendChild(button);

    return () => {
      containerRef.current?.removeChild(button);
    };
  }, [loaded, isDarkMode]);

  return (
    <div
      ref={containerRef}
      className={classnames(container, className, {
        'pointer-events-none opacity-50': disabled,
        '!h-[43px]': isMobileApp,
      })}
    >
      {loading && (
        <div className={loadingOverlay}>
          <LoadingIndicator lg={18} md={18} sm={18} customColor={Theme.color.white.DEFAULT} />
        </div>
      )}
    </div>
  );
}

const container = 'w-full h-[48px] relative';
const loadingOverlay = 'absolute inset-0 flex items-center justify-center z-10';
